"use client";

import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader, ShieldCheck, PhoneCall } from "lucide-react";
const PAYPAL_CLIENT_ID = "BAAc_KYI8iASL7nw6FIYNQykITp2_uzXR8j3spmeX3TW6yoEsPLroAnAFYPlV7Yfg2nWEi8JA6AfYfuQKs";
const DEFAULT_FORM_VALUES = {
  name: "",
  phone: "",
  email: "",
  service: "",
  message: ""
};
export default function FastResponseQuoteRequest() {
  return <section data-section-id="240051" id="terminal-interface" className="bg-background text-foreground py-16 md:py-24 px-4 relative overflow-hidden scroll-mt-24">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/10 blur-[120px]" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-widest mb-4 text-primary uppercase">
            Initialize Repair Protocol
          </h2> 
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            System diagnostic required? Submit your hardware parameters below for an immediate technician response.
          </p>
        </div>

        <div className="max-w-[600px] mx-auto">
          <div className="bg-card border border-primary/30 shadow-3xl rounded-lg overflow-hidden">
            <div className="bg-muted/50 border-b border-primary/20 p-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="size-3 rounded-lg bg-destructive/50" />
                <div className="size-3 rounded-lg bg-yellow-500/50" />
                <div className="size-3 rounded-lg bg-primary/50" />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground ml-2 uppercase tracking-widest">sway_systems_v1.0.4</span>
            </div>
            
            <div className="p-6 md:p-8 px-4 sm:px-6 md:px-8 lg:px-12">
              <FormProvider formKey="booking_form_home" formId="booking_form_home" sectionName="terminal-interface" className="space-y-0" defaultValues={DEFAULT_FORM_VALUES} validationRules={{
              name: {
                required: true
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              email: {
                required: true,
                pattern: "email"
              },
              service: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send My Request" successMessage="Your $25 deposit was received and your request has been sent.">
                <QuoteForm />
              </FormProvider>
              
              <div className="mt-6 pt-4 border-t border-primary/10">
                <p className="text-[10px] font-mono text-primary/60 animate-pulse">
                  We'll respond within one business day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const [mode, setMode] = useState<'book' | 'ask'>('book');
  const [submittedMode, setSubmittedMode] = useState<'book' | 'ask'>('book');
  const [depositPaid, setDepositPaid] = useState(false);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const hasScrolledRef = useRef(false);
  const lastAppliedServiceRef = useRef<string | null>(null);
  const activeReassertIntervalRef = useRef<any>(null);
  const paypalButtonsRef = useRef<any>(null);
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    resetForm,
    control,
    setValue,
    getValues
  } = useWvcForm();
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const applyServiceValue = (value: string) => {
    if (!value) return;
    if (setValue) {
      setValue('service', value, {
        shouldValidate: true,
        shouldDirty: false
      });
    }
    let matchCount = 0;
    let totalChecks = 0;
    const intervalId = setInterval(() => {
      totalChecks++;
      if (getValues && setValue) {
        const currentVal = getValues('service');
        if (currentVal !== value) {
          setValue('service', value, {
            shouldValidate: true,
            shouldDirty: false
          });
          matchCount = 0;
        } else {
          matchCount++;
        }
      }
      if (matchCount >= 3 || totalChecks >= 30) {
        clearInterval(intervalId);
      }
    }, 100);
    return intervalId;
  };
  useEffect(() => {
    let rafId: number;
    const pollServiceParam = () => {
      const serviceParam = new URLSearchParams(window.location.search).get('service');
      if (!serviceParam) return;
      let decodedValue: string;
      try {
        decodedValue = decodeURIComponent(serviceParam);
      } catch {
        decodedValue = serviceParam;
      }
      if (!decodedValue || decodedValue === lastAppliedServiceRef.current) return;
      const isInitialDetection = lastAppliedServiceRef.current === null;
      lastAppliedServiceRef.current = decodedValue;
      if (activeReassertIntervalRef.current) {
        clearInterval(activeReassertIntervalRef.current);
      }
      activeReassertIntervalRef.current = applyServiceValue(decodedValue);
      if (isInitialDetection && !hasScrolledRef.current) {
        let lastTop: number | null = null;
        let stableFrames = 0;
        const checkAndScroll = () => {
          const element = document.getElementById('terminal-interface');
          if (!element) {
            rafId = requestAnimationFrame(checkAndScroll);
            return;
          }
          const currentTop = element.getBoundingClientRect().top;
          if (lastTop !== null && Math.abs(currentTop - lastTop) < 1) {
            stableFrames++;
            if (stableFrames >= 2) {
              hasScrolledRef.current = true;
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
              return;
            }
          } else {
            stableFrames = 0;
          }
          lastTop = currentTop;
          rafId = requestAnimationFrame(checkAndScroll);
        };
        rafId = requestAnimationFrame(checkAndScroll);
      }
    };
    pollServiceParam();
    const pollInterval = setInterval(pollServiceParam, 250);
    return () => {
      clearInterval(pollInterval);
      if (activeReassertIntervalRef.current) {
        clearInterval(activeReassertIntervalRef.current);
      }
      cancelAnimationFrame(rafId);
    };
  }, []);
  useEffect(() => {
    if (depositPaid || isSubmitted) return;
    const scriptId = "paypal-sdk-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    const renderButtons = () => {
      const container = document.getElementById("paypal-button-container");
      if ((window as any).paypal && container) {
        container.innerHTML = "";
        try {
          const buttonsInstance = (window as any).paypal.Buttons({
            createOrder: (_data: any, actions: any) => {
              if (actions && actions.order) {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      currency_code: "USD",
                      value: "25.00"
                    }
                  }]
                });
              }
              return {
                purchase_units: [{
                  amount: {
                    currency_code: "USD",
                    value: "25.00"
                  }
                }]
              };
            },
            onApprove: async (_data: any, _actions: any) => {
              setDepositPaid(true);
              setPaypalError(null);
              setSubmittedMode('book');
              handleSubmitRef.current();
            },
            onError: (_err: any) => {
              setPaypalError("Payment processing error. Please try again or contact support.");
            },
            onCancel: () => {}
          });
          paypalButtonsRef.current = buttonsInstance;
          buttonsInstance.render("#paypal-button-container");
        } catch (err) {
          console.error("PayPal button rendering error:", err);
        }
      }
    };
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
      script.async = true;
      script.onload = () => {
        renderButtons();
      };
      document.body.appendChild(script);
    } else if ((window as any).paypal) {
      renderButtons();
    } else {
      script.addEventListener("load", renderButtons);
    }
    return () => {
      try {
        if (paypalButtonsRef.current && typeof paypalButtonsRef.current.close === "function") {
          Promise.resolve(paypalButtonsRef.current.close()).catch(() => {});
        }
      } catch {}
      paypalButtonsRef.current = null;
    };
  }, [depositPaid, isSubmitted]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 text-primary rounded-lg flex items-center justify-center mb-2 shadow-md">
          <ShieldCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold font-default text-primary tracking-wider">REQUEST SENT</h3>
        <p className="text-muted-foreground max-w-md font-default text-sm">
          {submittedMode === 'book' ? 'Your $25 deposit was received and your request has been sent.' : 'Your request has been sent.'}
        </p>
        <Button onClick={() => {
        resetForm();
        setDepositPaid(false);
      }} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/10 font-default rounded-lg">
          RE-INITIALIZE
        </Button>
      </div>;
  }
  return <Form onSubmit={e => {
    e.preventDefault();
    setSubmittedMode(mode);
    handleSubmit();
  }} className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
        <Button type="button" onClick={() => setMode('book')} variant={mode === 'book' ? 'default' : 'outline'} className={`flex-1 min-w-0 w-full sm:w-auto min-h-[44px] px-3 py-3 sm:py-2 text-center whitespace-normal break-words leading-tight uppercase font-default font-bold text-xs sm:text-sm tracking-widest rounded-lg transition-all ${mode === 'book' ? 'bg-primary text-primary-foreground shadow-md' : 'border-primary/30 text-muted-foreground hover:text-primary hover:border-primary/60 hover:bg-primary/10 bg-transparent'}`}>
          BOOK A REPAIR (DEPOSIT REQUIRED)
        </Button>
        <Button type="button" onClick={() => setMode('ask')} variant={mode === 'ask' ? 'default' : 'outline'} className={`flex-1 min-w-0 w-full sm:w-auto min-h-[44px] px-3 py-3 sm:py-2 text-center whitespace-normal break-words leading-tight uppercase font-default font-bold text-xs sm:text-sm tracking-widest rounded-lg transition-all ${mode === 'ask' ? 'bg-primary text-primary-foreground shadow-md' : 'border-primary/30 text-muted-foreground hover:text-primary hover:border-primary/60 hover:bg-primary/10 bg-transparent'}`}>
          ASK A QUESTION (NO DEPOSIT)
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-base sm:text-lg md:text-lg lg:text-lg text-primary/80 uppercase tracking-widest">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter Name..." {...field} aria-required="true" className="w-full bg-transparent border-0 border-b border-primary/30 rounded-lg focus-visible:ring-0 focus-visible:border-primary transition-all font-default" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-base sm:text-lg md:text-lg lg:text-lg text-primary/80 uppercase tracking-widest">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Phone Number..." {...field} aria-required="true" className="w-full bg-transparent border-0 border-b border-primary/30 rounded-lg focus-visible:ring-0 focus-visible:border-primary transition-all font-default" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base sm:text-lg md:text-lg lg:text-lg text-primary/80 uppercase tracking-widest">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email Address..." {...field} aria-required="true" className="w-full bg-transparent border-0 border-b border-primary/30 rounded-lg focus-visible:ring-0 focus-visible:border-primary transition-all font-default" />
            </FormControl>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <FormField control={control} name="service" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base sm:text-lg md:text-lg lg:text-lg text-primary/80 uppercase tracking-widest">What Do You Need Fixed?</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger aria-required="true" className="w-full bg-transparent border-0 border-b border-primary/30 rounded-lg focus:ring-0 focus:border-primary font-default">
                  <SelectValue placeholder="Select Protocol...">{field.value || undefined}</SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-primary/30 font-default rounded-lg">
                {["Basic PC Tune-Up", "Full System Cleanup", "File Backup Help", "Windows Updates & Fixes", "Complete PC Help Bundle", "New PC Setup & Transfer", "Hardware Repair", "Screen Replacement", "Remote Support", "Windows OS Installer", "Linux OS Installer", "Basic PC Cleaning", "Deep PC Cleaning", "Deep Clean + Thermal Paste", "Fan Replacement", "Cable Management", "Basic Console Clean", "Internal Dust Cleaning", "Deep Clean + Thermal Paste", "Controller Cleaning", "Console Hardware Repair", "Full Console Bundle"].map((service, i) => <SelectItem key={i} value={service} data-index={i} className="focus:bg-primary/20 focus:text-primary">
                    {service}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please describe the problem"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base sm:text-lg md:text-lg lg:text-lg text-primary/80 uppercase tracking-widest">Describe The Problem</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us what's going on with your device..." aria-required="true" className="w-full min-h-[100px] resize-y bg-transparent border-0 border-b border-primary/30 rounded-lg focus-visible:ring-0 focus-visible:border-primary transition-all font-default" {...field} />
            </FormControl>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <div className={mode === 'book' ? "border border-primary/30 rounded-lg overflow-hidden bg-card/40" : "hidden"}>
        <div className="bg-muted/50 border-b border-primary/20 p-3">
          <h4 className="text-xs sm:text-sm font-default uppercase tracking-widest text-primary font-bold">
            $25 DEPOSIT TO CONFIRM BOOKING
          </h4>
          <p className="text-xs text-muted-foreground font-default mt-1">
            A small deposit secures your repair slot.
          </p>
        </div>
        <div className="p-4">
          {depositPaid && <div className="flex items-center gap-2 text-primary font-mono text-sm py-2">
              <ShieldCheck className="size-5" />
              <span>Deposit Received</span>
            </div>}

          <div id="paypal-button-container" className={mode === 'book' && !depositPaid ? 'min-h-[45px]' : 'hidden'} />

          {paypalError && <div className="mt-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-3">
              <ShieldCheck className="size-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-xs font-mono text-destructive uppercase">{paypalError}</p>
            </div>}
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-3">
          <ShieldCheck className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs font-mono text-destructive uppercase">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" size="lg" disabled={isSubmitting || mode === "book" && !depositPaid} className="w-full h-14 text-base sm:text-lg md:text-lg lg:text-lg font-bold font-default rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all tracking-widest">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              EXECUTING...
            </> : <>
              <PhoneCall className="mr-2 size-5" />
              Send My Request
            </>}
        </Button>

        {mode === 'book' && !depositPaid && <p className="font-mono text-[10px] text-primary/60 mt-2 text-center uppercase tracking-wider">
            Complete the $25 deposit above to send your request
          </p>}
        
        <div className="flex items-center justify-center gap-2 mt-4 text-[10px] font-mono text-primary/40 uppercase tracking-widest">
          <ShieldCheck className="size-3" />
          <span>Encryption Active. Data Secure.</span>
        </div>
      </div>
    </Form>;
}